import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, CheckCircle, Lightbulb, AlertCircle, Save } from 'lucide-react';

interface DevelopmentObjective {
  id: string;
  focus: string;
  reflections?: Array<{
    id: string;
    date: string;
    note: string;
  }>;
}

interface Reflection {
  id: string;
  date: string;
  note: string;
}

interface ReflectionModalProps {
  objective: DevelopmentObjective;
  onClose: () => void;
  onSave: (reflection: Reflection) => void;
}

const ReflectionModal = ({ objective, onClose, onSave }: ReflectionModalProps) => {
  const { t } = useTranslation();
  const [reflectionNote, setReflectionNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);

  const SOFT_LIMIT = 500;
  const characterCount = reflectionNote.length;
  const isApproachingLimit = characterCount > SOFT_LIMIT * 0.8;
  const isOverLimit = characterCount > SOFT_LIMIT;

  const handleSave = async () => {
    if (!reflectionNote.trim()) return;

    setIsSaving(true);
    // Simulate save
    await new Promise((resolve) => setTimeout(resolve, 500));

    const reflection: Reflection = {
      id: `refl_${Date.now()}`,
      date: new Date().toISOString(),
      note: reflectionNote,
    };

    setShowSaveConfirmation(true);
    await new Promise((resolve) => setTimeout(resolve, 800));

    onSave(reflection);
    setIsSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col relative">
        {/* Save Confirmation Animation */}
        {showSaveConfirmation && (
          <div className="absolute inset-0 bg-green-500/90 flex items-center justify-center z-10 rounded-xl">
            <div className="text-center">
              <CheckCircle className="w-16 h-16 text-white mx-auto mb-4" />
              <p className="text-white text-lg font-semibold">{t('Reflection Saved!')}</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">{t('Add Reflection')}</h2>
              <p className="text-sm text-gray-600">{t('Document what you tried, what changed, or insights gained')}</p>
            </div>
            <button onClick={onClose} className="text-gray-600 hover:text-gray-900 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Objective Context */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-900 mb-1">{t('Development Objective')}</p>
            <p className="text-sm text-gray-600">{objective.focus}</p>
          </div>

          {/* Reflection Input */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">{t('Your Reflection')}</label>
            <textarea
              value={reflectionNote}
              onChange={(e) => setReflectionNote(e.target.value)}
              placeholder={t(
                'What did I try?\n\nWhat surprised me?\n\nWhat would I do differently?\n\nExample: Led a cross-functional meeting this week. Tried using the \'parking lot\' technique for off-topic discussions. Team stayed more focused, and we finished 15 minutes early. Would prepare more visual aids next time to help explain technical concepts to non-technical stakeholders.'
              )}
              className="w-full h-64 px-4 py-3 border border-border rounded-lg bg-white text-gray-900 placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
            />
            <div className="flex items-center justify-between mt-2">
              <p
                className={`text-xs transition-colors ${
                  isOverLimit ? 'text-red-600 font-semibold' : isApproachingLimit ? 'text-orange-600' : 'text-gray-600'
                }`}
              >
                {characterCount} {t('characters')}
                {isApproachingLimit && (
                  <span className="ml-2">
                    ({SOFT_LIMIT - characterCount} {t('until suggested limit')})
                  </span>
                )}
              </p>
              {isOverLimit && (
                <p className="text-xs text-red-600 flex items-center space-x-1">
                  <AlertCircle className="w-3 h-3" />
                  <span>{t('Consider being more concise')}</span>
                </p>
              )}
            </div>
          </div>

          {/* Tips */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <Lightbulb className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 mb-2">{t('Reflection Tips')}</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• {t('Be specific about actions you took and their outcomes')}</li>
                  <li>• {t('Include both successes and challenges - both are valuable')}</li>
                  <li>• {t('Note what support or resources helped you')}</li>
                  <li>• {t('Consider how this experience connects to your overall growth')}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Previous Reflections Count */}
          {objective.reflections && objective.reflections.length > 0 && (
            <div className="mt-4 flex items-center space-x-2 text-sm text-gray-600">
              <Lightbulb className="w-4 h-4" />
              <span>
                {t('You have')} {objective.reflections.length} {t('previous reflection')}
                {objective.reflections.length !== 1 ? 's' : ''} {t('on this objective')}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex items-center justify-end space-x-3">
          <button onClick={onClose} disabled={isSaving} className="px-4 py-2 border border-border rounded-lg hover:bg-gray-50 disabled:opacity-50">
            {t('Cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={!reflectionNote.trim() || isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isSaving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Save className="w-4 h-4" />
            )}
            {t('Save Reflection')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReflectionModal;
